<script setup lang="ts">
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { router, ROUTES } from '@/router.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { MAX_PROFILES } from '@/constants/EditMaxes.ts';
import NavHeader from '@/components/menu/NavHeader.vue';
import { ProfileConfigService } from '@/services/config/ProfileConfigService.ts';
import { LastConfigRemainingError } from '@/errors/edit/LastConfigRemainingError.ts';
import { EditLimitReachedError } from '@/errors/edit/EditLimitReachedError.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import { showAlertDialogAsync } from '@/wrappers/ShowAlertDialogAsync.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { NameInUseError } from '@/errors/edit/NameInUseError.ts';
import { ValidationFailedError } from '@/errors/edit/ValidationFailedError.ts';
import { Hotkey } from '@/types/Hotkey.ts';
import HotkeyIndicator from '@/components/hotkey/HotkeyIndicator.vue';
import { Button } from '@/components/ui/button';
import {
    CopyIcon,
    EllipsisIcon,
    InfoIcon,
    PencilIcon,
    PenLineIcon,
    PlusIcon,
    Trash2Icon,
} from 'lucide-vue-next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { showInputDialogAsync } from '@/wrappers/ShowInputDialogAsync.ts';
import { InvalidInputError } from '@/errors/edit/InvalidInputError.ts';
import { onMounted, onUnmounted } from 'vue';
import { eventEmitter, type HotkeyPressPayload } from '@/lib/eventEmitter.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';

/* region Setup */
const settingsStore = useSettingsStore();
/* endregion Setup */

/* region Profile mutation */
const createProfile = async () => {
    let profileName = '';
    try {
        profileName = await showInputDialogAsync({
            title: 'Create a new profile',
            description: 'Enter a name for the new profile',
            label: 'Profile name',
            submitText: 'Create',
        });
    } catch {}

    if (!profileName) {
        return;
    }

    let newProfileConfigId;
    try {
        const {
            profileConfigs,
            stepConfigs,
            promptConfigs,
            newProfileConfigId: localNewProfileConfigId,
        } = ProfileConfigService.createProfile(
            profileName,
            ProfileConfigService.getAllProfileConfigs(),
            StepConfigService.getAllStepConfigs(),
            PromptConfigService.getAllPromptConfigs(),
        );
        ProfileConfigService.updateProfileConfigs(profileConfigs);
        StepConfigService.updateStepConfigs(stepConfigs);
        PromptConfigService.updatePromptConfigs(promptConfigs);
        newProfileConfigId = localNewProfileConfigId;
    } catch (error) {
        if (error instanceof EditLimitReachedError) {
            createTextToast(
                'Max profiles reached',
                `A maximum of ${MAX_PROFILES} profiles can be created.`,
                'destructive',
                false,
            );
        } else if (error instanceof InvalidInputError) {
            createTextToast(
                'Input error',
                `The profile name ${profileName} is already being used`,
                'destructive',
                false,
            );
        } else {
            throw error;
        }
        return;
    }

    visitEditUpdatePage(newProfileConfigId);
};

const renameProfile = async (id: ProfileConfig['id']) => {
    const profileConfig = ProfileConfigService.getProfileConfig(id);
    if (!profileConfig) {
        return;
    }

    if (!profileConfig.editable) {
        createTextToast(
            'You cannot rename default profiles',
            'Please duplicate the profile and rename the copy instead.',
            'destructive',
            false,
        );
        return;
    }

    let newProfileName = '';
    try {
        newProfileName = await showInputDialogAsync({
            title: 'Rename profile',
            description: 'Enter a new name for the profile',
            label: 'Profile name',
            defaultValue: profileConfig.name,
            submitText: 'Rename',
        });
    } catch {}

    if (!newProfileName) {
        return;
    }

    try {
        const { profileConfigs } = ProfileConfigService.updateProfileName(
            ProfileConfigService.getAllProfileConfigs(),
            id,
            newProfileName.trim(),
        );
        ProfileConfigService.updateProfileConfigs(profileConfigs);
    } catch (error) {
        if (error instanceof NameInUseError) {
            createTextToast('Profile name already in use', error.message, 'destructive', false);
        } else if (error instanceof ValidationFailedError) {
            createTextToast('Profile name validation failed', error.message, 'destructive', false);
        } else {
            throw error;
        }
    }
};

const duplicateProfile = (id: ProfileConfig['id']) => {
    try {
        const { profileConfigs, stepConfigs, promptConfigs } =
            ProfileConfigService.duplicateProfile(
                ProfileConfigService.getAllProfileConfigs(),
                StepConfigService.getAllStepConfigs(),
                PromptConfigService.getAllPromptConfigs(),
                id,
            );
        ProfileConfigService.updateProfileConfigs(profileConfigs);
        StepConfigService.updateStepConfigs(stepConfigs);
        PromptConfigService.updatePromptConfigs(promptConfigs);
    } catch (error) {
        if (error instanceof EditLimitReachedError) {
            createTextToast(
                'Max profiles reached',
                `A maximum of ${MAX_PROFILES} profiles can be created.`,
                'destructive',
                false,
            );
        } else {
            throw error;
        }
        return;
    }
};

const deleteProfile = async (id: ProfileConfig['id']) => {
    const profileConfig = ProfileConfigService.getProfileConfig(id);
    if (!profileConfig) {
        return;
    }

    if (!profileConfig.editable) {
        createTextToast('You cannot delete default profiles', undefined, 'destructive', false);
        return;
    }

    const userConfirmed = await showAlertDialogAsync({
        title: `Are you sure you want to delete the "${profileConfig.name}" profile?`,
        actionText: 'Delete',
    });

    if (!userConfirmed) {
        return;
    }

    try {
        const { profileConfigs } = ProfileConfigService.deleteProfile(
            ProfileConfigService.getAllProfileConfigs(),
            id,
        );
        ProfileConfigService.updateProfileConfigs(profileConfigs);
    } catch (error) {
        if (error instanceof LastConfigRemainingError) {
            createTextToast(
                'Cannot delete last profile',
                'You must have at least one profile.',
                'destructive',
                false,
            );
        } else {
            throw error;
        }
        return;
    }

    // Remove from favorites
    if (settingsStore.favoriteProfileIds.includes(id)) {
        settingsStore.favoriteProfileIds = settingsStore.favoriteProfileIds.filter((p) => p !== id);
    }

    // Unselect as globally selected
    if (settingsStore.selectedProfileId === id) {
        settingsStore.selectedProfileId = Object.keys(
            ProfileConfigService.getAllProfileConfigs(),
        )[0];
    }
};
/* endregion Profile mutation */

/* region Favorites */
const addProfileToFavorites = (id: ProfileConfig['id']) => {
    settingsStore.favoriteProfileIds = [...settingsStore.favoriteProfileIds, id];
};

const removeProfileFromFavorites = (id: ProfileConfig['id']) => {
    settingsStore.favoriteProfileIds = settingsStore.favoriteProfileIds.filter((p) => p !== id);
};

/* region Other */
const visitEditUpdatePage = (id: ProfileConfig['id']) => {
    const profileConfig = ProfileConfigService.getProfileConfig(id);
    if (!profileConfig) {
        return;
    }

    if (!profileConfig.editable) {
        createTextToast(
            'You cannot edit default profiles',
            'Please duplicate the profile and edit the copy instead.',
            'destructive',
            false,
        );
        return;
    }

    router.push({
        name: ROUTES.editUpdate,
        params: {
            profileConfigId: profileConfig.id,
        },
    });
};

const onHotkeyPress = (payload: HotkeyPressPayload) => {
    if (payload.hotkey === Hotkey.Left) {
        router.push({ name: ROUTES.index });
    }
};
/* endregion Other */

/* region Lifecycle */
onMounted(() => {
    eventEmitter.addListener('hotkeyPress', onHotkeyPress);
});

onUnmounted(() => {
    eventEmitter.removeListener('hotkeyPress', onHotkeyPress);
});
/* endregion Lifecycle */
</script>

<template>
    <div class="flex flex-1 flex-col p-4 pt-0">
        <NavHeader>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink as-child class="flex gap-x-1.5">
                            <RouterLink :to="ROUTES.index">
                                Home
                                <HotkeyIndicator :hotkey="Hotkey.Left" />
                            </RouterLink>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>Profiles</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
        </NavHeader>

        <Card>
            <CardHeader>
                <CardTitle class="flex justify-between">
                    Profiles
                    <Button variant="default" @click="createProfile">
                        <PlusIcon />
                        New profile
                    </Button>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <p class="mb-6 text-sm text-muted-foreground">
                    Custom profiles allow you to create your own prompts and fully configure how you
                    use Leetcode Wizard.
                </p>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead class="w-[140px] px-0">
                                <div class="flex">
                                    <TooltipProvider :delay-duration="150">
                                        <Tooltip>
                                            <TooltipTrigger as-child class="flex items-center">
                                                <div>
                                                    Favorited <InfoIcon class="ms-1.5 size-4" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p class="flex">
                                                    You can toggle between favorited profiles with
                                                    the
                                                    <HotkeyIndicator
                                                        :hotkey="Hotkey.ActionX5"
                                                        class="mx-1"
                                                    />
                                                    hotkey.
                                                </p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </TableHead>
                            <TableHead class="w-[300px]"> Name </TableHead>
                            <TableHead class="text-right"> Actions </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow
                            v-for="profileConfig in ProfileConfigService.getAllProfileConfigs()"
                            :key="profileConfig.id"
                        >
                            <TableCell>
                                <template
                                    v-if="
                                        settingsStore.favoriteProfileIds.includes(profileConfig.id)
                                    "
                                >
                                    <TooltipProvider :delay-duration="150">
                                        <Tooltip>
                                            <TooltipTrigger as-child>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    @click="
                                                        removeProfileFromFavorites(profileConfig.id)
                                                    "
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="28"
                                                        height="28"
                                                        viewBox="0 0 24 24"
                                                        fill="currentColor"
                                                        stroke="currentColor"
                                                        stroke-width="3"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        class="lucide lucide-star text-yellow-500"
                                                    >
                                                        <path
                                                            d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
                                                        />
                                                    </svg>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent> Remove from favorites </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </template>
                                <template v-else>
                                    <TooltipProvider :delay-duration="150">
                                        <Tooltip>
                                            <TooltipTrigger as-child>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    @click="addProfileToFavorites(profileConfig.id)"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="28"
                                                        height="28"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        stroke-width="3"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        class="lucide lucide-star"
                                                    >
                                                        <path
                                                            d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
                                                        />
                                                    </svg>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent> Add to favorites </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </template>
                            </TableCell>
                            <TableCell class="font-medium">
                                {{ profileConfig.name }}
                                <Badge
                                    v-if="!profileConfig.editable"
                                    class="ms-2 rounded-sm"
                                    variant="secondary"
                                    >Default</Badge
                                >
                                <Badge
                                    v-if="profileConfig.id === settingsStore.selectedProfileId"
                                    class="ms-2 rounded-sm"
                                    variant="default"
                                    >Active</Badge
                                >
                            </TableCell>
                            <TableCell class="flex justify-end">
                                <div class="flex">
                                    <TooltipProvider :delay-duration="150">
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <Button
                                                    @click="visitEditUpdatePage(profileConfig.id)"
                                                    variant="outline"
                                                    size="icon"
                                                    class="rounded-e-none border-e-0"
                                                >
                                                    <PencilIcon />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Edit</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                class="rounded-s-none"
                                            >
                                                <EllipsisIcon />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent>
                                            <DropdownMenuItem
                                                @click="renameProfile(profileConfig.id)"
                                            >
                                                <PenLineIcon />Rename
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                @click="duplicateProfile(profileConfig.id)"
                                            >
                                                <CopyIcon />Duplicate
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                @click="deleteProfile(profileConfig.id)"
                                            >
                                                <Trash2Icon /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    </div>
</template>
